import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { Panel } from "../../components/ui/Panel";
import {
  moved,
  useReorder,
  type ReorderHandlers,
} from "../../components/ui/useReorder";
import type {
  BuiltinFieldRow,
  CrmFieldType,
  CustomFieldDefinition,
  PicklistRow,
  QuickTask,
  TaskRule,
  TaskTrigger,
} from "../../lib/crm-types";
import { Button } from "../../components/ui/Button";
import { cn } from "../../components/ui/cn";
import { AtRiskSettings } from "../settings/AtRiskSettings";
import { ArchiveSettings } from "./ArchiveSettings";
import { WebhookSettings } from "./WebhookSettings";

/**
 * Manager settings: the fields every company carries, and the options behind
 * the four built-in dropdowns.
 *
 * Both are edited here because they are the same decision wearing two hats --
 * "what do we record about an account, and what are the allowed answers" -- and
 * splitting them across two screens would mean nobody finds the second one.
 *
 * The built-in fields are here too. They are columns on `companies` rather
 * than rows, which had made them the one thing a manager could not touch --
 * they can now be renamed, reordered and hidden, but never deleted, and the
 * remove control says so rather than being absent.
 *
 * A field's type and a field's deletion were both refused outright until
 * recently, on the grounds that either would orphan values agents had entered.
 * Both are now allowed with the loss made visible instead: a conversion names
 * every value it would clear, and a deletion names the field it is about to
 * take. Deleting is final -- there was a day-long restore window and it is
 * gone -- so hiding is offered first and is what "get rid of it" should
 * usually mean.
 */
const LIST_LABELS: Record<string, string> = {
  customer_type: "Customer type",
  lifecycle_stage: "Lifecycle stage",
  lead_priority: "Lead priority",
  lead_source: "Lead source",
};

/**
 * The CRM's sections, without a page around them.
 *
 * Exported on its own because these now live as a tab inside the platform's one
 * settings page rather than behind a separate "CRM settings" link. See
 * `pages/Settings.tsx` for why: a role is not per product, and two settings
 * pages implied that it was.
 */
export function CrmSettingsSections({ isAdmin }: { isAdmin: boolean }) {
  return (
    /*
      Collapsible panels, matching the Helpdesk tab beside this one. Five
      sections stacked open made a page you scrolled past to reach the one you
      came for; the Helpdesk tab solved that a while ago and there was no
      reason for the CRM's to look different.
    */
    <>
      <Panel collapsible title="Company list columns">
        <ListColumns isAdmin={isAdmin} />
      </Panel>
      {/*
        First among the rules panels, because it decides what the account
        manager dashboard shows everybody -- who is lapsing and who to ring.
        Its own component rather than another section in this file: it is
        thresholds per sales team and shares nothing with the field editors.
      */}
      <AtRiskSettings isAdmin={isAdmin} />
      {/*
        One panel, not two. "Custom fields" and "Dropdown options" were
        siblings, which made the option lists look like a separate feature
        rather than what they are -- the choices behind a field that happens to
        be a dropdown. Somebody adding a lifecycle stage is thinking about
        Stage, so the options are edited on Stage.

        It also has to hold the built-in fields, which previously had no home
        at all: City and VAT number are columns rather than rows, so the only
        way to stop using one was to leave it sitting there.
      */}
      <Panel collapsible title="Customise fields">
        <CustomiseFields />
      </Panel>
      <Panel collapsible title="Quick tasks">
        <QuickTasks />
      </Panel>
      <Panel collapsible title="Suggested tasks">
        <TaskRules />
      </Panel>
      {/* Admins only, like the routes behind it: archiving takes companies
          out of everybody's view. */}
      {isAdmin ? (
        <Panel collapsible title="Tidy up">
          <ArchiveSettings />
        </Panel>
      ) : null}
      {/* Last, and deliberately: it configures who else hears about all of the
          above, which only makes sense once the record itself does. */}
      <Panel collapsible title="Webhooks">
        <WebhookSettings />
      </Panel>
    </>
  );
}

// ── Company list columns ─────────────────────────────────────────────────────

/**
 * Which scope of the layout is being edited.
 *
 * A tagged union rather than a string plus a nullable team id, so "team" can
 * never be selected without one -- which would have been a save posted to
 * `/teams/undefined`.
 */
type ColumnScope =
  | { kind: "mine" }
  | { kind: "team"; id: number; name: string }
  | { kind: "desk" };

/**
 * Which columns the company list shows, at whichever level.
 *
 * There are three, resolved personal -> team -> desk, and this one panel edits
 * all of them because they are the same decision at different scopes: a
 * manager setting up Internal Sales and a rep adding one column for the week
 * are doing the same thing to the same list, and two screens for it would mean
 * nobody finds the second.
 *
 * ## Why the desk-wide one is no longer the whole story
 *
 * It used to be, on the argument that two people comparing accounts must be
 * looking at the same columns. That argument is right about the wrong unit --
 * internal sales and support read this list for different reasons, and the
 * columns that serve one are dead weight to the other -- so it is now made per
 * team, which is where people actually compare notes.
 *
 * ## What everybody may edit
 *
 * Their own, always. Their team's and the desk's, only as an admin: those
 * change what other people see, which is the same rule as before, applied to
 * the two layers it still makes sense for. A non-admin can still *read* the
 * other layers, because "why does my list look like this" deserves an answer.
 *
 * Order is part of the setting, so the control is a chosen list with drag
 * handles rather than a set of checkboxes -- a checkbox grid can say which
 * columns but never which order, and left-to-right order is most of what makes
 * a table readable.
 */
function ListColumns({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [scope, setScope] = useState<ColumnScope>({ kind: "mine" });
  const [draft, setDraft] = useState<string[] | null>(null);

  const view = useQuery({
    queryKey: queryKeys.crmListView("companies"),
    queryFn: () => crmApi.listView("companies"),
    staleTime: 60_000,
  });

  const teamViews = useQuery({
    queryKey: queryKeys.crmTeamListViews("companies"),
    queryFn: () => crmApi.teamListViews("companies"),
    staleTime: 60_000,
  });

  /*
   * Everything a write here could have changed.
   *
   * The resolved layout, every team's row, and every rendered company list --
   * because saving a team's columns changes what its members' lists show, and
   * naming the three keys is cheaper than one of them being missed.
   */
  const invalidate = (): void => {
    setDraft(null);
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crmListView("companies"),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crmTeamListViews("companies"),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.crmCompaniesRoot,
    });
  };

  /*
   * The three routes answer with different shapes -- a resolved layout, a
   * layout, or the list of team layouts -- and none of them is used: the
   * invalidation above refetches whatever is on screen. Discarded here rather
   * than plumbed through, so the mutation has one return type and the three
   * branches do not have to agree on a response they do not read.
   */
  const save = useMutation({
    mutationFn: async (columns: string[]): Promise<void> => {
      if (scope.kind === "mine") {
        await crmApi.saveMyListView(columns);
      } else if (scope.kind === "team") {
        await crmApi.saveTeamListView(scope.id, columns);
      } else {
        await crmApi.saveListView(columns);
      }
    },
    onSuccess: invalidate,
  });

  /** Forget this scope's own layout, so it inherits again. */
  const reset = useMutation({
    mutationFn: async (): Promise<void> => {
      if (scope.kind === "mine") {
        await crmApi.resetMyListView();
      } else if (scope.kind === "team") {
        await crmApi.resetTeamListView(scope.id);
      } else {
        /* Unreachable: the button is not rendered on the desk-wide scope,
           which has nothing under it but the built-in defaults. Thrown rather
           than silently doing nothing, so a future caller finds out. */
        throw new Error("The desk-wide layout has nothing to inherit from.");
      }
    },
    onSuccess: invalidate,
  });

  const layers = view.data?.layers;
  const teams = teamViews.data ?? [];

  /** This scope's own row, or null when it has none and is inheriting. */
  const stored: string[] | null =
    scope.kind === "mine"
      ? (layers?.personal ?? null)
      : scope.kind === "team"
        ? (teams.find((entry) => entry.teamId === scope.id)?.columns ?? null)
        : (layers?.desk ?? null);

  /*
   * What to show when this scope has no row of its own: whatever it currently
   * inherits, so editing starts from what is actually on screen rather than
   * from an empty list. A team with none inherits the desk's; the viewer with
   * none sees the already-resolved answer, which is exactly `view.columns`.
   */
  const inherited: string[] =
    scope.kind === "team"
      ? (layers?.desk ?? view.data?.columns ?? [])
      : (view.data?.columns ?? []);

  const chosen = draft ?? stored ?? inherited;
  const available = view.data?.available ?? [];
  const dirty = draft !== null;
  const editable = scope.kind === "mine" || isAdmin;

  const labelFor = (key: string): string =>
    available.find((column) => column.key === key)?.label ?? key;

  /* Into the draft, not straight to the server: this list has an explicit
     Save because it changes what every agent sees. */
  const reorder = useReorder((from, to) => setDraft(moved(chosen, from, to)));

  /* Switching scope abandons an unsaved draft, deliberately. Carrying it
     across would mean the columns on screen belong to the scope you just left,
     and Save would write them somewhere nobody chose. */
  const choose = (next: ColumnScope): void => {
    setDraft(null);
    save.reset();
    reset.reset();
    setScope(next);
  };

  return (
    <section>
      {/*
        Whose columns are being edited. Tabs rather than a dropdown because
        there are rarely more than four and the set is the point: seeing
        "Everyone / Internal sales / Support / Just me" laid out is how
        somebody learns the layers exist at all.
      */}
      <div className="mb-3 flex flex-wrap items-center gap-1">
        <ScopeTab
          label="Just me"
          active={scope.kind === "mine"}
          set={layers?.personal != null}
          onClick={() => choose({ kind: "mine" })}
        />
        {teams.map((team) => (
          <ScopeTab
            key={team.teamId}
            label={team.teamName}
            active={scope.kind === "team" && scope.id === team.teamId}
            set={team.columns != null}
            onClick={() =>
              choose({ kind: "team", id: team.teamId, name: team.teamName })
            }
          />
        ))}
        <ScopeTab
          label="Everyone"
          active={scope.kind === "desk"}
          set={layers?.desk != null}
          onClick={() => choose({ kind: "desk" })}
        />
      </div>

      {/*
        One sentence saying what the selected scope actually does, and where
        this layer sits. Without it the tabs are four words with no stated
        consequence, and "Everyone" in particular reads as harmless right up
        until somebody presses Save.
      */}
      <p className="mb-2 text-xs text-muted">
        {scope.kind === "mine" ? (
          <>
            Your own columns, over the top of everything below.{" "}
            {stored
              ? "You have set your own, so changes to your team's no longer reach you."
              : view.data?.source === "team" && view.data.team
                ? `You are currently following ${view.data.team.name}'s.`
                : "You are currently following the desk's."}
          </>
        ) : scope.kind === "team" ? (
          <>
            What everybody in {scope.name} sees, unless they have set their own.{" "}
            {stored ? "" : "This team has none yet and follows the desk's."}
          </>
        ) : (
          <>
            The fallback for every team and person who has not set their own.
            Changing it moves anybody still following it.
          </>
        )}
      </p>

      <div className="mb-2 flex items-center gap-2">
        {/* Unsaved and unmistakably so, rather than a quiet button beside the
            heading. Adding a column happens at the bottom right of the panel
            below; the Save that appeared up here, a screen away from the
            click, was routinely missed -- and a layout change that looks done
            and is not reads afterwards as "I saved it and nothing happened". */}
        {dirty ? (
          <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300">
            Not saved yet
          </span>
        ) : null}

        {/* Said where the controls are, not only by disabling them. A row of
            dead buttons with no explanation reads as a broken page. */}
        {!editable ? (
          <span className="text-[11px] text-faint">
            Read-only — an admin sets this one.
          </span>
        ) : null}

        {/* Only when there is something to reset *to*. On the desk-wide layer
            there is nothing underneath, so the button never appears there. */}
        {editable && stored && scope.kind !== "desk" ? (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            disabled={reset.isPending}
            onClick={() => reset.mutate()}
          >
            {reset.isPending
              ? "Resetting…"
              : scope.kind === "mine"
                ? view.data?.team
                  ? `Follow ${view.data.team.name}'s again`
                  : "Follow the desk's again"
                : "Follow the desk's again"}
          </Button>
        ) : null}
      </div>

      <div className="grid gap-4 rounded-lg border border-line p-3 lg:grid-cols-2">
        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-faint">
            Shown, in order
          </p>
          {/* Company is first and not removable -- without it a row cannot be
              opened, which is the only thing every reader needs to do. */}
          <div className="mb-1 flex items-center gap-2 rounded border border-dashed border-line px-2 py-1 text-xs text-faint">
            Company
            <span className="ml-auto text-[11px]">always first</span>
          </div>

          <ul className="flex flex-col gap-1">
            {chosen.map((key, index) => (
              <li
                key={key}
                {...reorder.row(index)}
                className={cn(
                  "flex items-center gap-1 rounded border border-line px-2 py-1 text-xs text-content",
                  reorder.dragging === index && "opacity-40",
                  reorder.over === index &&
                    reorder.dragging !== index &&
                    "border-t-2 border-t-brand-500",
                )}
              >
                <span
                  {...reorder.handle(index)}
                  role="button"
                  aria-label={`Drag ${labelFor(key)} to reorder`}
                  title="Drag to reorder"
                  className="cursor-grab select-none text-faint active:cursor-grabbing"
                >
                  ⠿
                </span>
                {labelFor(key)}
                <span className="ml-auto flex items-center gap-0.5">
                  <IconButton
                    label={`Remove ${labelFor(key)}`}
                    onClick={() =>
                      setDraft(chosen.filter((entry) => entry !== key))
                    }
                  >
                    ✕
                  </IconButton>
                </span>
              </li>
            ))}
            {chosen.length === 0 ? (
              <li className="text-xs text-faint">
                No columns. The list will show company names alone.
              </li>
            ) : null}
          </ul>
        </div>

        <div>
          <p className="mb-1 text-[11px] uppercase tracking-wide text-faint">
            Available
          </p>
          <ul className="flex flex-wrap gap-1">
            {available
              .filter((column) => !chosen.includes(column.key))
              .map((column) => (
                <li key={column.key}>
                  <button
                    type="button"
                    onClick={() => setDraft([...chosen, column.key])}
                    className="rounded-full border border-line px-2 py-0.5 text-[11px] text-muted hover:border-brand-300 hover:text-brand-600 dark:hover:text-brand-400"
                  >
                    + {column.label}
                  </button>
                </li>
              ))}
          </ul>
          <p className="mt-2 text-[11px] text-faint">
            Custom fields appear here as soon as they are created, so anything a
            manager records about an account can be a column.
          </p>
        </div>
      </div>

      {/*
        The save, directly under the thing being edited and spanning it, so it
        is where the eye already is after adding or moving a column. It is a
        bar rather than a button because the sentence beside it is half the
        point: this writes over what the whole desk sees, and nothing has
        happened until it is pressed.
      */}
      {dirty ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-brand-300 bg-brand-50 px-3 py-2 dark:bg-brand-950/40">
          <span className="text-xs text-content">
            {chosen.length} column{chosen.length === 1 ? "" : "s"} chosen — not
            saved yet.
          </span>
          <Button
            size="sm"
            className="ml-auto"
            disabled={save.isPending || !editable}
            onClick={() => save.mutate(chosen)}
          >
            {/* The button says who it writes for. "Save" alone on a panel with
                four tabs is how somebody sets the whole desk's columns while
                believing they were setting their own. */}
            {save.isPending
              ? "Saving…"
              : scope.kind === "mine"
                ? "Save for me"
                : scope.kind === "team"
                  ? `Save for ${scope.name}`
                  : "Save for everyone"}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setDraft(null)}>
            Discard
          </Button>
        </div>
      ) : null}

      {save.isError || reset.isError ? (
        <p className="mt-2 text-xs text-sla-breached">
          {(save.error ?? reset.error) instanceof ApiError
            ? (save.error ?? reset.error)!.message
            : "Could not save the layout."}
        </p>
      ) : null}
    </section>
  );
}

/**
 * One scope in the row above the editor.
 *
 * `set` marks the scopes that have a layout of their own, so a manager can see
 * at a glance which teams have been given one and which are still following
 * the desk. Without it the tabs all look alike and the only way to find out is
 * to click each one.
 */
function ScopeTab({
  label,
  active,
  set,
  onClick,
}: {
  label: string;
  active: boolean;
  /** This scope has a saved layout rather than inheriting one. */
  set: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={set ? `${label} has its own columns` : `${label} inherits`}
      className={cn(
        "flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors",
        active
          ? "bg-brand-600 text-white"
          : "text-muted hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {label}
      {set ? (
        <span
          aria-hidden
          className={cn(
            "h-1.5 w-1.5 rounded-full",
            active ? "bg-white/80" : "bg-brand-500",
          )}
        />
      ) : null}
    </button>
  );
}

function IconButton({
  children,
  label,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-5 w-5 items-center justify-center rounded text-[11px] text-faint",
        disabled ? "opacity-30" : "hover:bg-surface-2 hover:text-brand-600 dark:hover:text-brand-400",
      )}
    >
      {children}
    </button>
  );
}

// ── Custom fields ────────────────────────────────────────────────────────────

/**
 * The fields every company carries.
 *
 * A list of one-line rows that open. Adding a field puts a new line at the top
 * of that same list, already open -- so making one and editing one are the same
 * gesture in the same place, rather than a form that appears somewhere else.
 *
 * Everything inside an open row is a draft until Save. That is the opposite of
 * the Helpdesk's settings, which commit on blur, and deliberately: a dropdown's
 * options are edited as a set, and saving each keystroke would mean a field
 * briefly having an option called "Saf" while somebody types "SafeContractor".
 */
/** Headings for the built-in groups, in the order they are shown. */
const FIELD_GROUPS: { key: string; label: string; note?: string }[] = [
  { key: "account", label: "The account" },
  { key: "classification", label: "How it is classified" },
  { key: "registration", label: "Registration" },
  {
    key: "credit",
    label: "Credit file",
    note: "Filled in from an ordered credit report, so these cannot be edited by hand.",
  },
  { key: "activity", label: "Activity" },
];

/**
 * Every field on a company, in one editor.
 *
 * Three kinds, deliberately shown as one list, because to a manager they are
 * one thing -- "the fields on a company" -- and the difference between a
 * column and a row is an implementation detail they should not have to hold in
 * their head.
 *
 * The difference that *does* surface is what can be deleted. A built-in stays
 * in the schema whatever this page does, so its remove control is shown
 * disabled with a tooltip saying why, rather than omitted. An absent control
 * reads as an oversight; a disabled one is an answer.
 */
function CustomiseFields() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  /** Which existing row is open. One at a time: they are tall when open. */
  const [openId, setOpenId] = useState<number | null>(null);
  /** Which built-in has its options or rename open, by key. */
  const [openKey, setOpenKey] = useState<string | null>(null);

  const settings = useQuery({
    queryKey: queryKeys.crmFieldSettings,
    queryFn: crmApi.fieldSettings,
    staleTime: 60_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmFieldSettings });
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmFields });
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmPicklists });
    /* The column picker and the filter bar are both drawn from these, and a
       renamed or hidden field has to leave them at the same moment. */
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmListView("companies") });
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmFilters });
    // Every company page renders these, so their cached details are now stale.
    void queryClient.invalidateQueries({ queryKey: ["crm", "company"] });
  };

  const data = settings.data;
  const list = data?.custom ?? [];

  if (settings.isLoading) {
    return <p className="text-xs text-faint">Loading fields…</p>;
  }

  return (
    <section className="space-y-5">
      {/* ── Built-ins ───────────────────────────────────────────────────── */}
      {FIELD_GROUPS.map((group) => {
        const fields = (data?.builtins ?? []).filter(
          (field) => field.group === group.key,
        );
        if (fields.length === 0) return null;

        return (
          <div key={group.key}>
            <h3 className="mb-1.5 text-[11px] uppercase tracking-wide text-faint">
              {group.label}
            </h3>
            {group.note ? (
              <p className="mb-1.5 text-[11px] text-faint">{group.note}</p>
            ) : null}

            <ul className="divide-y divide-line rounded-lg border border-line">
              {fields.map((field) => (
                <BuiltinFieldEditor
                  key={field.key}
                  field={field}
                  options={
                    field.picklist ? (data?.picklists[field.picklist] ?? []) : []
                  }
                  open={openKey === field.key}
                  onToggle={() =>
                    setOpenKey((current) =>
                      current === field.key ? null : field.key,
                    )
                  }
                  onChange={refresh}
                />
              ))}
            </ul>
          </div>
        );
      })}

      {/* ── Custom fields ───────────────────────────────────────────────── */}
      <div>
        <h3 className="mb-1.5 text-[11px] uppercase tracking-wide text-faint">
          Your own fields
        </h3>
        <CustomFieldsList
          list={list}
          adding={adding}
          setAdding={setAdding}
          openId={openId}
          setOpenId={setOpenId}
          refresh={refresh}
        />
      </div>
    </section>
  );
}

/**
 * One built-in: rename it, hide it, and edit its options if it has any.
 *
 * Renaming is inline and saves on blur rather than behind an edit button. The
 * only thing that can be changed here is one short string, and a form around a
 * single input is ceremony.
 */
function BuiltinFieldEditor({
  field,
  options,
  open,
  onToggle,
  onChange,
}: {
  field: BuiltinFieldRow;
  options: PicklistRow[];
  open: boolean;
  onToggle: () => void;
  onChange: () => void;
}) {
  const [label, setLabel] = useState(field.label);

  /* Follows the server when somebody else renames it, but not while this
     person is mid-edit -- which is what a bare `value={field.label}` would do
     on every refetch. */
  useEffect(() => setLabel(field.label), [field.label]);

  const save = useMutation({
    mutationFn: (patch: { label?: string | null; isActive?: boolean }) =>
      crmApi.updateBuiltinField(field.key, patch),
    onSuccess: onChange,
  });

  const rename = (): void => {
    const next = label.trim();
    if (next === field.label) return;
    /* Empty means "put it back", not "call it nothing" -- there is no useful
       field with a blank label, and it is the obvious way to undo a rename. */
    save.mutate({ label: next === "" ? null : next });
  };

  return (
    <li className={cn("px-3 py-1.5", field.isHidden && "opacity-50")}>
      <div className="flex items-center gap-2">
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={rename}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.key === "Escape") setLabel(field.label);
          }}
          aria-label={`Rename ${field.label}`}
          className="h-7 min-w-0 flex-1 rounded border border-transparent bg-transparent px-1.5 text-xs text-content outline-none hover:border-line focus:border-brand-400 focus:bg-app"
        />

        {field.isRenamed ? (
          <span className="shrink-0 text-[10px] text-faint" title="Renamed from the built-in label">
            renamed
          </span>
        ) : null}

        <span className="shrink-0 text-[10px] uppercase tracking-wide text-faint">
          {field.kind}
        </span>

        {/* Options live on the field that uses them, not in a separate panel. */}
        {field.picklist ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="shrink-0 text-[11px] text-brand-600 hover:text-brand-700"
          >
            {open ? "Done" : `${options.filter((o) => o.isActive).length} options`}
          </button>
        ) : null}

        <button
          type="button"
          disabled={save.isPending}
          onClick={() => save.mutate({ isActive: field.isHidden })}
          title={
            field.isHidden
              ? "Show this field again. Nothing was lost while it was hidden."
              : "Hide this field from the company page, the columns and the filters. The data stays."
          }
          className="shrink-0 text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          {field.isHidden ? "Show" : "Hide"}
        </button>

        {/*
          Shown and disabled, not omitted. This is a column on `companies`;
          deleting it is a migration rather than a setting, and saying so where
          somebody looks for the control is better than leaving a gap they
          read as a bug.
        */}
        <button
          type="button"
          disabled
          aria-label={`${field.label} cannot be deleted`}
          title="Built-in fields cannot be deleted — reporting is built on them. Hide it instead."
          className="shrink-0 cursor-not-allowed text-faint opacity-40"
        >
          ✕
        </button>
      </div>

      {open && field.picklist ? (
        <div className="mt-2">
          <ListEditor
            listKey={field.picklist}
            options={options}
            onChange={onChange}
          />
        </div>
      ) : null}
    </li>
  );
}

function CustomFieldsList({
  list,
  adding,
  setAdding,
  openId,
  setOpenId,
  refresh,
}: {
  list: CustomFieldDefinition[];
  adding: boolean;
  setAdding: (value: boolean | ((current: boolean) => boolean)) => void;
  openId: number | null;
  setOpenId: (value: number | null | ((current: number | null) => number | null)) => void;
  refresh: () => void;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            setAdding((value) => !value);
            setOpenId(null);
          }}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          {adding ? "Cancel" : "Add custom field"}
        </button>
      </div>

      <ul className="divide-y divide-line rounded-lg border border-line">
        {/* The new line, directly under the button that made it. */}
        {adding ? (
          <li className="bg-app">
            <NewFieldRow
              onDone={() => {
                setAdding(false);
                refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          </li>
        ) : null}

        {list.length === 0 && !adding ? (
          <li className="px-3 py-4 text-xs text-faint">
            No custom fields. Add one and it appears on every company.
          </li>
        ) : (
          list.map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              open={openId === field.id}
              onToggle={() =>
                setOpenId((current) => (current === field.id ? null : field.id))
              }
              onChange={refresh}
            />
          ))
        )}
      </ul>
    </section>
  );
}

/**
 * One field: a summary line that opens into its editor.
 *
 * Retire stays on the summary line rather than inside the editor. It is the one
 * action taken on a field without wanting to change anything about it, and
 * burying it a click deep would make the common case the slow one.
 */
function FieldRow({
  field,
  open,
  onToggle,
  onChange,
}: {
  field: CustomFieldDefinition;
  open: boolean;
  onToggle: () => void;
  onChange: () => void;
}) {
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof crmApi.updateField>[1]) =>
      crmApi.updateField(field.id, patch),
    onSuccess: onChange,
  });

  return (
    <li className={cn(!field.isActive && "opacity-50")}>
      <div className="flex items-center gap-2 px-3 py-2">
        {/*
          The summary is the disclosure control, so the whole line is the target
          rather than a small chevron. Retire is a sibling, not nested: a button
          inside a button is invalid and unpredictable under a screen reader.
        */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span
            aria-hidden
            className={cn(
              "shrink-0 text-[11px] text-faint transition-transform",
              open && "rotate-90",
            )}
          >
            ▶
          </span>
          <span className="truncate text-sm text-content">{field.label}</span>
          <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase text-faint">
            {field.type}
          </span>
          {field.type === "dropdown" ? (
            <span className="truncate text-[11px] text-faint">
              {(field.options ?? []).length} option
              {(field.options ?? []).length === 1 ? "" : "s"}
            </span>
          ) : null}
        </button>

        {/*
          Hide and delete are different acts and say so. Hiding is indefinite
          and reversible at any time; deleting is neither. They were one control
          -- an "✕" that only hid -- which meant there was no way at all to get
          rid of a field created by mistake, and a settings page that
          accumulates other people's typos is one nobody trusts.

          Which is why Hide comes first, and reads as the ordinary answer: it is
          what somebody wants almost every time they reach for either.
        */}
        <button
          type="button"
          onClick={() => update.mutate({ isActive: !field.isActive })}
          disabled={update.isPending}
          title={
            field.isActive
              ? "Hides it from every company. Values already entered are kept."
              : "Shows it again, with the values it had."
          }
          className="shrink-0 text-[11px] text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          {field.isActive ? "Hide" : "Show"}
        </button>

        <DeleteFieldButton field={field} onDeleted={onChange} />
      </div>

      {open ? (
        <>
          <FieldEditor field={field} onSaved={onChange} onClose={onToggle} />
          {/*
            Outside the editor's form, deliberately. It has its own mutation
            and its own confirm step, and a submit button inside that form
            would save the label instead of converting anything.
          */}
          <div className="border-t border-line bg-app px-3 py-3">
            <TypeChanger field={field} onChanged={onChange} />
          </div>
        </>
      ) : null}
    </li>
  );
}

/**
 * Delete, with the confirmation inline rather than in a `window.confirm`.
 *
 * Two clicks, and this is now the only thing standing between the click and a
 * field that is not coming back -- there was a day-long restore window behind
 * it and there is not any more. So the confirm says what actually happens
 * rather than "delete?", and says it where the pointer already is: a native
 * modal steals focus and puts the warning somewhere nobody was looking.
 *
 * Still two clicks rather than a typed confirmation. The click it guards
 * against is a misplaced one, and a misplaced click does not survive being
 * asked a question; making a manager type a field's name to tidy up a typo
 * would be ceremony aimed at the wrong mistake.
 */
function DeleteFieldButton({
  field,
  onDeleted,
}: {
  field: CustomFieldDefinition;
  onDeleted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  const remove = useMutation({
    mutationFn: () => crmApi.deleteField(field.id),
    onSuccess: () => {
      setConfirming(false);
      onDeleted();
    },
  });

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        title="Delete this field for good. To keep the values, hide it instead."
        aria-label={`Delete ${field.label}`}
        className="shrink-0 text-xs text-muted hover:text-sla-breached"
      >
        ✕
      </button>
    );
  }

  return (
    <span className="flex shrink-0 items-center gap-1.5">
      {/* Named, not "Delete?". The row it sits on is one of several, they are
          the same width, and the wrong one being confirmed is exactly the
          mistake there is no longer an undo for. */}
      <span className="text-[11px] text-faint">
        Delete {field.label} for good?
      </span>
      <button
        type="button"
        disabled={remove.isPending}
        onClick={() => remove.mutate()}
        className="text-[11px] text-sla-breached hover:underline"
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="text-[11px] text-muted hover:text-content"
      >
        No
      </button>
    </span>
  );
}

/**
 * Changing a field's type, with what it costs shown first.
 *
 * The preview is not optional politeness -- the server refuses a change whose
 * casualty count disagrees with what it last reported, so this component
 * cannot skip it. That is deliberate: somebody who saw "2 values will be
 * cleared", went away, and came back after ninety more rows were filled in
 * would otherwise destroy eighty-eight values nobody showed them.
 */
function TypeChanger({
  field,
  onChanged,
}: {
  field: CustomFieldDefinition;
  onChanged: () => void;
}) {
  const [target, setTarget] = useState<CrmFieldType | null>(null);
  const [options, setOptions] = useState<string[]>([]);

  const preview = useQuery({
    queryKey: ["crm", "field-type-preview", field.id, target, options],
    queryFn: () => crmApi.previewFieldType(field.id, target!, options),
    enabled: target !== null,
  });

  const apply = useMutation({
    mutationFn: () =>
      crmApi.changeFieldType(
        field.id,
        target!,
        options,
        preview.data?.casualties.length ?? 0,
      ),
    onSuccess: () => {
      setTarget(null);
      setOptions([]);
      onChanged();
    },
  });

  const plan = preview.data;

  return (
    <div className="rounded border border-line p-2">
      <label className="flex items-center gap-2 text-xs">
        <span className="text-muted">Type</span>
        <select
          value={target ?? field.type}
          onChange={(event) => {
            const next = event.target.value as CrmFieldType;
            setTarget(next === field.type ? null : next);
            /* Converting freehand text to a dropdown almost always wants the
               answers already in the field as the option list, so they are
               offered pre-selected rather than retyped. */
            setOptions([]);
          }}
          className="h-7 rounded border border-line bg-app px-2 text-xs text-content outline-none focus:border-brand-400"
        >
          {(["text", "number", "dropdown", "date"] as const).map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </label>

      {target && target === "dropdown" && plan ? (
        <div className="mt-2">
          <p className="text-[11px] text-faint">
            Options. The values already in this field are offered — tick the
            ones that should become choices.
          </p>
          <ul className="mt-1 flex flex-wrap gap-1">
            {plan.distinct.map((value) => {
              const chosen = options.includes(value);
              return (
                <li key={value}>
                  <button
                    type="button"
                    onClick={() =>
                      setOptions((current) =>
                        chosen
                          ? current.filter((entry) => entry !== value)
                          : [...current, value],
                      )
                    }
                    className={cn(
                      "rounded-full border px-2 py-0.5 text-[11px]",
                      chosen
                        ? "border-brand-400 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                        : "border-line text-muted hover:border-brand-300",
                    )}
                  >
                    {value}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {target && plan ? (
        <div className="mt-2 text-[11px]">
          {plan.casualties.length === 0 ? (
            <p className="text-muted">
              Nothing is lost — every value converts.
            </p>
          ) : (
            <>
              <p className="text-sla-breached">
                {plan.casualties.length} value
                {plan.casualties.length === 1 ? "" : "s"} cannot be converted
                and will be cleared:
              </p>
              {/* Named, not counted. "3 values will be cleared" is a number
                  somebody clicks past; the values themselves are things they
                  recognise and go and fix. */}
              <ul className="mt-0.5 max-h-24 overflow-y-auto text-faint">
                {plan.casualties.slice(0, 20).map((casualty) => (
                  <li key={casualty.companyId}>
                    {casualty.companyName ?? `company #${casualty.companyId}`}:{" "}
                    {casualty.value}
                  </li>
                ))}
                {plan.casualties.length > 20 ? (
                  <li>…and {plan.casualties.length - 20} more</li>
                ) : null}
              </ul>
            </>
          )}

          <div className="mt-1.5 flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={
                apply.isPending ||
                (target === "dropdown" && options.length === 0)
              }
              onClick={() => apply.mutate()}
            >
              Change to {target}
            </Button>
            <button
              type="button"
              onClick={() => setTarget(null)}
              className="text-[11px] text-muted hover:text-content"
            >
              Cancel
            </button>
          </div>

          {apply.isError ? (
            <p className="mt-1 text-sla-breached">
              {apply.error instanceof ApiError
                ? apply.error.message
                : "Could not change the type."}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The open half of a field row: label, hint, and a dropdown's options. */
function FieldEditor({
  field,
  onSaved,
  onClose,
}: {
  field: CustomFieldDefinition;
  onSaved: () => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(field.label);
  const [helpText, setHelpText] = useState(field.helpText ?? "");
  const [options, setOptions] = useState<string[]>(field.options ?? []);

  const save = useMutation({
    mutationFn: () =>
      crmApi.updateField(field.id, {
        label: label.trim(),
        helpText: helpText.trim() || null,
        ...(field.type === "dropdown"
          ? { options: options.map((option) => option.trim()).filter(Boolean) }
          : {}),
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const cleaned = options.map((option) => option.trim()).filter(Boolean);

  const dirty =
    label.trim() !== field.label ||
    (helpText.trim() || null) !== (field.helpText ?? null) ||
    (field.type === "dropdown" &&
      cleaned.join("\u0000") !== (field.options ?? []).join("\u0000"));

  const valid =
    label.trim().length > 0 &&
    (field.type !== "dropdown" || cleaned.length > 0);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-3 border-t border-line bg-app px-3 py-3"
    >
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Label
          </span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            className={fieldInput + " w-56"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Hint (optional)
          </span>
          <input
            value={helpText}
            onChange={(event) => setHelpText(event.target.value)}
            className={fieldInput + " w-64"}
          />
        </label>
      </div>

      {field.type === "dropdown" ? (
        <OptionsEditor options={options} onChange={setOptions} />
      ) : null}

      <div className="flex items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!dirty || !valid || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onClose}>
          Close
        </Button>

        {save.isError ? (
          <span className="text-xs text-sla-breached">
            {save.error instanceof ApiError
              ? save.error.message
              : "Could not save that field."}
          </span>
        ) : null}

        {/*
          The key is what every stored value is joined on, so it is shown and
          not editable -- a rename here would orphan every answer.
        */}
        <code className="ml-auto text-[11px] text-faint">{field.fieldKey}</code>
      </div>
    </form>
  );
}

/** The new field, as a line in the same list. */
function NewFieldRow({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomFieldDefinition["type"]>("text");
  const [helpText, setHelpText] = useState("");
  const [options, setOptions] = useState<string[]>([""]);

  const cleaned = options.map((option) => option.trim()).filter(Boolean);

  const create = useMutation({
    mutationFn: () =>
      crmApi.createField({
        label: label.trim(),
        type,
        options: type === "dropdown" ? cleaned : undefined,
        helpText: helpText.trim() || null,
      }),
    onSuccess: onDone,
  });

  const valid =
    label.trim().length > 0 && (type !== "dropdown" || cleaned.length > 0);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        create.mutate();
      }}
      className="flex flex-col gap-3 px-3 py-3"
    >
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Label
          </span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            autoFocus
            className={fieldInput + " w-56"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Type
          </span>
          <select
            value={type}
            onChange={(event) =>
              setType(event.target.value as CustomFieldDefinition["type"])
            }
            className={fieldInput}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="dropdown">Dropdown</option>
            <option value="date">Date</option>
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Hint (optional)
          </span>
          <input
            value={helpText}
            onChange={(event) => setHelpText(event.target.value)}
            className={fieldInput + " w-64"}
          />
        </label>
      </div>

      {type === "dropdown" ? (
        <OptionsEditor options={options} onChange={setOptions} />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!valid || create.isPending}>
          {create.isPending ? "Adding…" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        {create.isError ? (
          <span className="text-xs text-sla-breached">
            {create.error instanceof ApiError
              ? create.error.message
              : "Could not add that field."}
          </span>
        ) : null}

        <p className="w-full text-[11px] text-faint">
          The type cannot be changed afterwards: a text field holding “approx
          40” cannot become a number field without losing that value.
        </p>
      </div>
    </form>
  );
}

/**
 * Add, remove and edit a dropdown's options, one row each.
 *
 * One input per option rather than a comma-separated string. The string was
 * quicker to build and wrong in use: it cannot hold an option with a comma in
 * it, it gives no way to remove one except careful text surgery, and it shows a
 * long list as an unreadable run-on line.
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
function OptionsEditor({
  options,
  onChange,
}: {
  options: string[];
  onChange: (options: string[]) => void;
}) {
  const set = (index: number, value: string): void => {
    const next = [...options];
    next[index] = value;
    onChange(next);
  };

  const reorder = useReorder((from, to) => onChange(moved(options, from, to)));

  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-faint">
        Options
      </span>

      <ul className="flex flex-col gap-1">
        {options.map((option, index) => (
          <li
            key={index}
            {...reorder.row(index)}
            className={cn(
              "flex items-center gap-1",
              reorder.dragging === index && "opacity-40",
              reorder.over === index &&
                reorder.dragging !== index &&
                "border-t-2 border-t-brand-500",
            )}
          >
            <span
              {...reorder.handle(index)}
              role="button"
              aria-label={`Drag option ${index + 1} to reorder`}
              title="Drag to reorder"
              className="cursor-grab select-none text-faint active:cursor-grabbing"
            >
              ⠿
            </span>
            <input
              value={option}
              onChange={(event) => set(index, event.target.value)}
              placeholder="Option"
              aria-label={`Option ${index + 1}`}
              className={fieldInput + " w-64"}
            />
            <IconButton
              label={`Remove option ${index + 1}`}
              onClick={() => onChange(options.filter((_, at) => at !== index))}
            >
              ✕
            </IconButton>
          </li>
        ))}
      </ul>

      <div>
        <button
          type="button"
          onClick={() => onChange([...options, ""])}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          + Add option
        </button>
      </div>

      {/*
        Removing an option here is not the same as retiring one on a built-in
        list below: a company already set to it keeps the stored value, but the
        option stops being offered and nothing shows what the old value meant.
      */}
      <p className="text-[11px] text-faint">
        Companies already set to a removed option keep that value.
      </p>
    </div>
  );
}

const fieldInput =
  "h-8 rounded-md border border-line bg-surface px-2 text-sm text-content outline-none focus:border-brand-400";

// ── Quick tasks ──────────────────────────────────────────────────────────────

/**
 * The one-click buttons at the top of every company's Tasks section.
 *
 * Worth a manager's attention rather than each rep's, and not only for
 * consistency: because the label becomes the task's title, a shared set is what
 * makes "how many site surveys did we book last quarter" answerable at all. Ten
 * people typing their own version of the same job makes that question
 * unanswerable forever, and nobody notices until somebody asks it.
 *
 * These delete, unlike everything else on this page. Nothing points back at a
 * button once its task exists, so removing one loses a shortcut and no history
 * -- and a Retire that only hid a button would be a worse version of the same
 * thing.
 */
function QuickTasks() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const tasks = useQuery({
    queryKey: queryKeys.crmQuickTasks,
    queryFn: crmApi.quickTasks,
    staleTime: 60_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmQuickTasks });
  };

  /* Saves only the rows whose index changed, then refetches once. */
  const reorder = useReorder((from, to) => {
    const next = moved(list, from, to);
    void Promise.all(
      next
        .map((task, index) =>
          task.position === index
            ? null
            : crmApi.updateQuickTask(task.id, { position: index }),
        )
        .filter(Boolean),
    ).then(refresh);
  });

  const list = tasks.data ?? [];

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          {adding ? "Cancel" : "Add quick task"}
        </button>
      </div>

      <ul className="divide-y divide-line rounded-lg border border-line">
        {adding ? (
          <li className="bg-app">
            <QuickTaskForm
              onDone={() => {
                setAdding(false);
                refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          </li>
        ) : null}

        {list.length === 0 && !adding ? (
          <li className="px-3 py-4 text-xs text-faint">
            No quick tasks. Add the jobs this desk raises every week and they
            become one click on every company.
          </li>
        ) : (
          list.map((task, index) => (
            <QuickTaskRow
              key={task.id}
              task={task}
              index={index}
              reorder={reorder}
              onChange={refresh}
            />
          ))
        )}
      </ul>
    </section>
  );
}

function QuickTaskRow({
  task,
  index,
  reorder,
  onChange,
}: {
  task: QuickTask;
  index: number;
  reorder: ReorderHandlers;
  onChange: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  /* Reordering moved up to the list, which is the only thing that knows what
     the new order *is*. This row now only edits and deletes itself. */
  const remove = useMutation({
    mutationFn: () => crmApi.deleteQuickTask(task.id),
    onSuccess: onChange,
  });

  if (editing) {
    return (
      <li className="bg-app">
        <QuickTaskForm
          task={task}
          onDone={() => {
            setEditing(false);
            onChange();
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li
      {...reorder.row(index)}
      className={cn(
        "flex items-center gap-2 px-3 py-2",
        reorder.dragging === index && "opacity-40",
        reorder.over === index &&
          reorder.dragging !== index &&
          "border-t-2 border-t-brand-500",
      )}
    >
      <span
        {...reorder.handle(index)}
        role="button"
        aria-label={`Drag ${task.label} to reorder`}
        title="Drag to reorder"
        className="cursor-grab select-none text-faint active:cursor-grabbing"
      >
        ⠿
      </span>
      <span className="truncate text-sm text-content">{task.label}</span>

      {task.dueInDays !== null ? (
        <span className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-faint">
          {task.dueInDays === 0 ? "due today" : `due in ${task.dueInDays}d`}
        </span>
      ) : null}

      {task.description ? (
        <span className="truncate text-[11px] text-faint">
          {task.description}
        </span>
      ) : null}

      <span className="ml-auto flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="px-1 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Edit
        </button>

        {/* Two steps, because it really deletes and there is no undo. */}
        {confirming ? (
          <>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              className="px-1 text-xs text-sla-breached hover:underline"
            >
              {remove.isPending ? "Removing…" : "Remove"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="px-1 text-xs text-faint hover:text-muted"
            >
              Cancel
            </button>
          </>
        ) : (
          <IconButton
            label={`Remove ${task.label}`}
            onClick={() => setConfirming(true)}
          >
            ✕
          </IconButton>
        )}
      </span>
    </li>
  );
}

/** Add or edit one button. The same form either way. */
function QuickTaskForm({
  task,
  onDone,
  onCancel,
}: {
  task?: QuickTask;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(task?.label ?? "");
  const [description, setDescription] = useState(task?.description ?? "");
  const [dueInDays, setDueInDays] = useState(
    task?.dueInDays === null || task?.dueInDays === undefined
      ? ""
      : String(task.dueInDays),
  );

  const save = useMutation({
    mutationFn: () => {
      const input = {
        label: label.trim(),
        description: description.trim() || null,
        dueInDays: dueInDays === "" ? null : Number(dueInDays),
      };
      return task
        ? crmApi.updateQuickTask(task.id, input)
        : crmApi.createQuickTask(input);
    },
    onSuccess: onDone,
  });

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-3 px-3 py-3"
    >
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Button
          </span>
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Chase the quote"
            autoFocus
            className={fieldInput + " w-56"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Due in (days)
          </span>
          <input
            value={dueInDays}
            onChange={(event) => setDueInDays(event.target.value)}
            type="number"
            min={0}
            max={365}
            placeholder="No due date"
            className={fieldInput + " w-32"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Note on the task (optional)
          </span>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className={fieldInput + " w-72"}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!label.trim() || save.isPending}
        >
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        {save.isError ? (
          <span className="text-xs text-sla-breached">
            {save.error instanceof ApiError
              ? save.error.message
              : "Could not save that."}
          </span>
        ) : null}

        <p className="w-full text-[11px] text-faint">
          The button’s text becomes the task’s title in Lane, so keep it the
          same wording every time — it is what the counting is done on.
        </p>
      </div>
    </form>
  );
}

// ── Suggestion rules ─────────────────────────────────────────────────────────

const TRIGGER_LABELS: Record<TaskTrigger, string> = {
  no_activity: "Nothing has happened for",
  no_order: "No order placed in",
  quote_unconverted: "A quote has sat unconverted for",
};

/**
 * Standing rules that propose work nobody has thought to raise.
 *
 * A rule only ever *suggests*. Nothing here can create a task on its own, and
 * that is a deliberate ceiling rather than an unfinished feature: a threshold
 * set slightly too tight would otherwise fill every rep's board overnight with
 * work they did not choose, and the board is the thing they have to trust.
 */
function TaskRules() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);

  const rules = useQuery({
    queryKey: queryKeys.crmTaskRules,
    queryFn: crmApi.taskRules,
    staleTime: 60_000,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.crmTaskRules });
    // Every open company page is showing suggestions from the old rules.
    void queryClient.invalidateQueries({
      queryKey: ["crm", "task-suggestions"],
    });
  };

  const list = rules.data ?? [];

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setAdding((value) => !value)}
          className="text-xs text-brand-600 hover:text-brand-700"
        >
          {adding ? "Cancel" : "Add rule"}
        </button>
      </div>

      <ul className="divide-y divide-line rounded-lg border border-line">
        {adding ? (
          <li className="bg-app">
            <RuleForm
              onDone={() => {
                setAdding(false);
                refresh();
              }}
              onCancel={() => setAdding(false)}
            />
          </li>
        ) : null}

        {list.length === 0 && !adding ? (
          <li className="px-3 py-4 text-xs text-faint">
            No rules. A rule watches for an account going quiet and offers a
            task on its page — it never raises one by itself.
          </li>
        ) : (
          list.map((rule) => (
            <RuleRow key={rule.id} rule={rule} onChange={refresh} />
          ))
        )}
      </ul>
    </section>
  );
}

function RuleRow({ rule, onChange }: { rule: TaskRule; onChange: () => void }) {
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const update = useMutation({
    mutationFn: (patch: Parameters<typeof crmApi.updateTaskRule>[1]) =>
      crmApi.updateTaskRule(rule.id, patch),
    onSuccess: onChange,
  });

  const remove = useMutation({
    mutationFn: () => crmApi.deleteTaskRule(rule.id),
    onSuccess: onChange,
  });

  if (editing) {
    return (
      <li className="bg-app">
        <RuleForm
          rule={rule}
          onDone={() => {
            setEditing(false);
            onChange();
          }}
          onCancel={() => setEditing(false)}
        />
      </li>
    );
  }

  const waiting = rule.trigger !== "no_activity";

  return (
    <li className={cn("px-3 py-2", !rule.isActive && "opacity-50")}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-content">{rule.name}</p>
          <p className="truncate text-[11px] text-faint">
            {TRIGGER_LABELS[rule.trigger]} {rule.thresholdDays}{" "}
            {rule.thresholdDays === 1 ? "day" : "days"}
            {rule.lifecycleStage ? ` · ${rule.lifecycleStage} only` : ""} →{" "}
            <span className="text-muted">{rule.taskTitle}</span>
          </p>
        </div>

        {/* Named on the rule itself, so a manager writing one today knows
            before saving that it will not fire until Team is connected. */}
        {waiting ? (
          <span
            title="Quotes and orders live in Team, which has no API yet. The rule is saved and starts working when that lands."
            className="shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase text-faint"
          >
            waiting on Team
          </span>
        ) : null}

        <button
          type="button"
          onClick={() => update.mutate({ isActive: !rule.isActive })}
          disabled={update.isPending}
          title={
            rule.isActive
              ? "Stops it suggesting anything. The rule is kept."
              : "Starts it suggesting again."
          }
          className="shrink-0 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          {rule.isActive ? "Pause" : "Resume"}
        </button>

        <button
          type="button"
          onClick={() => setEditing(true)}
          className="shrink-0 text-xs text-muted hover:text-brand-600 dark:hover:text-brand-400"
        >
          Edit
        </button>

        {confirming ? (
          <>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              className="shrink-0 text-xs text-sla-breached hover:underline"
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="shrink-0 text-xs text-faint hover:text-muted"
            >
              Cancel
            </button>
          </>
        ) : (
          <IconButton
            label={`Delete ${rule.name}`}
            onClick={() => setConfirming(true)}
          >
            ✕
          </IconButton>
        )}
      </div>
    </li>
  );
}

function RuleForm({
  rule,
  onDone,
  onCancel,
}: {
  rule?: TaskRule;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(rule?.name ?? "");
  const [trigger, setTrigger] = useState<TaskTrigger>(
    rule?.trigger ?? "no_activity",
  );
  const [thresholdDays, setThresholdDays] = useState(
    String(rule?.thresholdDays ?? 30),
  );
  const [lifecycleStage, setLifecycleStage] = useState(
    rule?.lifecycleStage ?? "",
  );
  const [taskTitle, setTaskTitle] = useState(rule?.taskTitle ?? "");
  const [taskDescription, setTaskDescription] = useState(
    rule?.taskDescription ?? "",
  );

  /* The stages a rule can be scoped to, from the same list the company page
     offers -- so a rule cannot be aimed at a stage nobody can be in. */
  const filters = useQuery({
    queryKey: queryKeys.crmFilters,
    queryFn: crmApi.filters,
    staleTime: 5 * 60_000,
  });

  const stages = filters.data?.picklists.lifecycle_stage ?? [];

  const save = useMutation({
    mutationFn: () => {
      const input = {
        name: name.trim(),
        trigger,
        thresholdDays: Number(thresholdDays),
        lifecycleStage: lifecycleStage || null,
        taskTitle: taskTitle.trim(),
        taskDescription: taskDescription.trim() || null,
      };
      return rule
        ? crmApi.updateTaskRule(rule.id, input)
        : crmApi.createTaskRule(input);
    },
    onSuccess: onDone,
  });

  const days = Number(thresholdDays);
  const valid =
    name.trim() !== "" &&
    taskTitle.trim() !== "" &&
    Number.isInteger(days) &&
    days >= 1;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
      className="flex flex-col gap-3 px-3 py-3"
    >
      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Rule name
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Customers going quiet"
            autoFocus
            className={fieldInput + " w-56"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            When
          </span>
          <select
            value={trigger}
            onChange={(event) => setTrigger(event.target.value as TaskTrigger)}
            className={fieldInput}
          >
            <option value="no_activity">Nothing has happened for…</option>
            <option value="no_order">No order placed in… (needs Team)</option>
            <option value="quote_unconverted">
              A quote has sat unconverted for… (needs Team)
            </option>
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Days
          </span>
          <input
            value={thresholdDays}
            onChange={(event) => setThresholdDays(event.target.value)}
            type="number"
            min={1}
            max={3650}
            className={fieldInput + " w-24"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Only for
          </span>
          <select
            value={lifecycleStage}
            onChange={(event) => setLifecycleStage(event.target.value)}
            className={fieldInput}
          >
            <option value="">Every company</option>
            {stages.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Suggest this task
          </span>
          <input
            value={taskTitle}
            onChange={(event) => setTaskTitle(event.target.value)}
            placeholder="Ring them and see what they need"
            className={fieldInput + " w-72"}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-faint">
            Note on the task (optional)
          </span>
          <input
            value={taskDescription}
            onChange={(event) => setTaskDescription(event.target.value)}
            className={fieldInput + " w-72"}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>

        {save.isError ? (
          <span className="text-xs text-sla-breached">
            {save.error instanceof ApiError
              ? save.error.message
              : "Could not save that rule."}
          </span>
        ) : null}

        {trigger !== "no_activity" ? (
          <p className="w-full text-[11px] text-faint">
            Quotes and orders are recorded on the timeline, but no rule reads
            them yet. This rule saves now, says so on every company page, and
            starts suggesting the day that lands — no second setup.
          </p>
        ) : (
          <p className="w-full text-[11px] text-faint">
            Counts notes, calls, meetings, tasks and ticket replies. An account
            with no history at all is measured from the day it was added.
          </p>
        )}
      </div>
    </form>
  );
}

// ── Picklists ────────────────────────────────────────────────────────────────

function ListEditor({
  listKey,
  options,
  onChange,
}: {
  listKey: string;
  options: PicklistRow[];
  onChange: () => void;
}) {
  const [label, setLabel] = useState("");

  const add = useMutation({
    mutationFn: () =>
      crmApi.addPicklistOption({ listKey, label: label.trim() }),
    onSuccess: () => {
      setLabel("");
      onChange();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      crmApi.updatePicklistOption(id, { isActive }),
    onSuccess: onChange,
  });

  return (
    <div className="rounded-lg border border-line">
      <header className="border-b border-line px-3 py-2 text-xs font-medium text-content">
        {LIST_LABELS[listKey] ?? listKey}
      </header>

      <ul className="divide-y divide-line">
        {options.map((option) => (
          <li
            key={option.id}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 text-xs",
              !option.isActive && "opacity-50",
            )}
          >
            <span className="flex-1 text-content">{option.label}</span>
            <code className="text-[11px] text-faint">{option.value}</code>
            <button
              type="button"
              disabled={update.isPending}
              onClick={() =>
                update.mutate({ id: option.id, isActive: !option.isActive })
              }
              title={
                option.isActive
                  ? "Stops it being chosen. Companies already set to it keep it."
                  : "Makes it choosable again."
              }
              aria-label={option.isActive ? "Retire" : "Restore"}
              className="text-muted hover:text-brand-600 dark:hover:text-brand-400"
            >
              {option.isActive ? "✕" : "Restore"}
            </button>
          </li>
        ))}
      </ul>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
        className="flex items-center gap-2 border-t border-line px-3 py-2"
      >
        <input
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Add an option…"
          aria-label={`Add an option to ${LIST_LABELS[listKey] ?? listKey}`}
          className="h-7 flex-1 rounded border border-line bg-app px-2 text-xs text-content outline-none focus:border-brand-400"
        />
        <Button
          type="submit"
          size="sm"
          disabled={add.isPending || !label.trim()}
        >
          Add
        </Button>
      </form>
    </div>
  );
}
